import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

type Params = Record<string, string>;

/** A name from the page's own data (an email list's description, a campaign's name); `fallback` until it is loaded. */
export type TitleName = {
    query: (params: Params) => UseQueryOptions<unknown, unknown, unknown, QueryKey>;
    select: (data: unknown) => string | undefined;
    fallback: string;
};

/**
 * A page title key, or one per value of a route param (a tab). An unknown value gets the first key: the tabbed pages
 * redirect to their first tab.
 */
export type TitlePage = string | { param: string; keys: Record<string, string> };

/**
 * A page route's document title, declared in its `staticData`. Translation keys, so a tenant's own wording applies;
 * DocumentTitle adds ` | site name`.
 * - `page`: the page title; with `section`, followed by it ("Password | My account"); with `forWebinar`, joined with
 *   the webinar by `Title.ForWebinar` ("{page} for {webinar}").
 * - `name`: a name from the page's data, joined the same way.
 * - `webinar`: a pattern of its own with `{{webinar}}` ("Register for {webinar}").
 * - `webinarTitle`: the webinar title alone.
 */
export type RouteTitle =
    | { page: TitlePage; section?: string; forWebinar?: true }
    | { name: TitleName; forWebinar?: true }
    | {
          webinar:
              'Title.RegisterForWebinar' | 'Title.WatchWebinar' | 'Title.EditWebinar' | 'Title.DeleteWebinar' | 'Title.ShareWebinar' | 'Title.BroadcastWebinar';
      }
    | { webinarTitle: true };

/** Types `select` against what `query` returns; DocumentTitle only passes one's data to the other. */
export const titleName = <TData, TError, TQueryKey extends QueryKey>(
    query: (params: Params) => UseQueryOptions<TData, TError, TData, TQueryKey>,
    select: (data: TData) => string | undefined,
    fallback: string
): TitleName => ({ query, select, fallback }) as unknown as TitleName;

declare module '@tanstack/router-core' {
    interface StaticDataRouteOption {
        // Optional: createFileRoute's options extend this too, and most routes in packages/router declare no
        // staticData. documentTitleCoverage.test.ts checks that every page route has one.
        title?: RouteTitle;
    }
}
